import urljoin from 'url-join';
import { Analytics, createAnalytics } from '../analytics/Analytics';
import { ApiEvent } from '../analytics/constants/enums';
import { CheckoutUXFlow } from '../enums/Checkout';
import { PaymentFlow } from '../enums/Tokenization';
import { PaymentMethodType } from '../enums/Tokens';
import {
  CheckoutOptions,
  ClientContextOptions,
  OrderDetails,
  PrimerClientCredentials,
  RenderOptions,
  SinglePaymentMethodCheckoutOptions,
  VaultManagerOptions,
} from '../types';
import { Nullable } from '../utilities';
import { Environment } from '../utils/Environment';
import { funcify } from '../utils/funcify';
import ModuleFactory from '../utils/ModuleFederation/ModuleFactory';
import Package from '../utils/ModuleFederation/Package';

import { ScriptLoader } from '../utils/ScriptLoader';
import { Api } from './Api';
import {
  createClientTokenHandler,
  IClientTokenHandler,
  DecodedClientToken,
} from './ClientTokenHandler';
import { IFrameEventType } from './IFrameEventType';
import { IFrameFactory } from './IFrameFactory';
import { IFrameMessageBus } from './IFrameMessageBus';
import { LongPoll } from './LongPoll';

interface PaymentMethodConfig {
  id: string;
  type: PaymentMethodType;
  options: {
    threeDSecureToken?: string;
    threeDSecureInitUrl?: string;
    threeDSecureProvider?: string;
    threeDSecureEnabled?: boolean;
  };
}

export interface CheckoutModuleConfig {
  type: string;
  requestUrl: string;
}

export interface ClientSessionInfo {
  assetsUrl: string;
  coreUrl: string;
  pciUrl: string;
  modulesUrl: string;
  env: string;
  production: boolean;
  paymentFlow: PaymentFlow;
  paymentMethods: PaymentMethodConfig[];
  checkoutModules: CheckoutModuleConfig[];
  threeDSecureToken: Nullable<string>;
  threeDSecureInitUrl: Nullable<string>;
  threeDSecureProvider: Nullable<string>;
  threeDSecureEnabled: boolean;
  accessToken: string;
  isTeardown: boolean;
}

export interface ClientContext {
  clientTokenHandler: IClientTokenHandler;
  iframes: IFrameFactory;
  messageBus: IFrameMessageBus;
  api: Api;
  longPoll: LongPoll;
  analytics: Analytics;
  scriptLoader: ScriptLoader;
  session: ClientSessionInfo;
  moduleFactory: ModuleFactory;
  packages: Package[];
}

interface ClientConfiguration {
  coreUrl: string;
  pciUrl: string;
  env: string;
  paymentMethods: PaymentMethodConfig[];
  checkoutModules: CheckoutModuleConfig[];
}

//TODO: Remove when all payment methods are in an external module
const paymentMethodsNotInExternalModule = [PaymentMethodType.PAYMENT_CARD];

export const ClientContextFactory = {
  async create({
    options,
    renderOptions,
  }: {
    options: ClientContextOptions;
    renderOptions:
      | CheckoutOptions
      | VaultManagerOptions
      | SinglePaymentMethodCheckoutOptions
      | RenderOptions;
  }): Promise<ClientContext> {
    const clientTokenHandler = createClientTokenHandler();

    const {
      accessToken,
      configurationUrl,
      analyticsUrl,
      paymentFlow,
    } = initializeClientTokenHandler(clientTokenHandler, options.credentials);

    const assetsUrl = getAssetsURL();

    const messageBus = new IFrameMessageBus();
    const iframes = new IFrameFactory({ messageBus, assetsUrl });
    const api = new Api({ iframes, messageBus, accessToken });
    const longPoll = new LongPoll({ api });

    clientTokenHandler.addNewClientTokenListener(
      ({ accessToken: newAccessToken }) => api.setAccessToken(newAccessToken),
    );

    let orderDetails: OrderDetails | undefined;

    if (renderOptions.uxFlow !== CheckoutUXFlow.MANAGE_PAYMENT_METHODS) {
      const checkoutOptions = renderOptions as CheckoutOptions;
      orderDetails = funcify<OrderDetails | undefined>(
        checkoutOptions.orderDetails,
      )();
    }

    let amount = orderDetails?.totalAmount;

    if (typeof amount === 'string') {
      amount = parseFloat(amount);
    }

    const analytics = createAnalytics(analyticsUrl);

    analytics.setContext({
      amount,
      currency: orderDetails?.currencyCode,
      isVault: renderOptions.uxFlow === CheckoutUXFlow.MANAGE_PAYMENT_METHODS,
    });

    analytics.call({ event: ApiEvent.startedCheckout });
    analytics.time({ event: ApiEvent.loadedCheckoutUi });
    analytics.time({ event: ApiEvent.completedCheckout });

    const { data, error } = await api.get<ClientConfiguration>(
      configurationUrl,
    );

    if (error) {
      throw new Error(`Failed to initialize client: ${error}`);
    }

    if (!data) {
      throw new Error('Failed to initialize client');
    }

    analytics.setSdkEnvironment(data.env);

    messageBus.publish('api-controller', {
      type: IFrameEventType.SESSION,
      payload: {
        coreUrl: data.coreUrl,
        pciUrl: data.pciUrl,
      },
    });

    const paymentMethods = filterPaymentMethodConfigs(
      data.paymentMethods,
      renderOptions,
    );

    const scriptLoader = new ScriptLoader(options);
    const moduleFactory = new ModuleFactory(scriptLoader);
    const modulesUrl = getModulesUrl();
    const packages = (
      await Promise.all(
        paymentMethods
          //TODO: Remove when all payment methods are in an external module
          .filter(
            (paymentMethod) =>
              !paymentMethodsNotInExternalModule.includes(paymentMethod.type),
          )
          //
          .map(async (paymentMethod) => {
            const moduleName = paymentMethod.type
              .toLowerCase()
              .replace('_', '-');
            const remotePath = urljoin(
              modulesUrl,
              '/payment-methods/',
              moduleName,
            );
            const pack = moduleFactory.getPackage({
              remotePath,
              scope: moduleName,
            });

            try {
              await pack.getDeclaration();
            } catch (e) {
              return null;
            }

            return pack;
          }),
      )
    ).filter((p) => !!p) as Package[];

    const cardPaymentMethod = data?.paymentMethods.find(
      (elm) => elm.type === PaymentMethodType.PAYMENT_CARD,
    );

    const { checkoutModules } = data;

    return {
      clientTokenHandler,
      iframes,
      messageBus,
      api,
      longPoll,
      analytics,
      scriptLoader,
      moduleFactory,
      packages,
      session: {
        isTeardown: false,
        assetsUrl,
        coreUrl: Environment.get('PRIMER_CORE_API_URL', data.coreUrl),
        pciUrl: Environment.get('PRIMER_PCI_API_URL', data.pciUrl),
        modulesUrl,
        env: data.env,
        production: data.env === 'PRODUCTION',
        paymentMethods,
        checkoutModules,
        paymentFlow: paymentFlow || PaymentFlow.DEFAULT,
        accessToken,

        // TODO: refactor the usage of 3DS tokens
        get threeDSecureToken(): Nullable<string> {
          return cardPaymentMethod?.options?.threeDSecureToken ?? null;
        },
        get threeDSecureInitUrl(): Nullable<string> {
          return cardPaymentMethod?.options?.threeDSecureInitUrl ?? null;
        },
        get threeDSecureProvider(): string {
          return cardPaymentMethod?.options?.threeDSecureProvider ?? 'CARDINAL';
        },
        get threeDSecureEnabled(): boolean {
          return (
            cardPaymentMethod?.options?.threeDSecureEnabled ??
            Boolean(cardPaymentMethod?.options?.threeDSecureInitUrl ?? null)
          );
        },
      },
    };
  },
};

function initializeClientTokenHandler(
  clientTokenHandler: IClientTokenHandler,
  credentials: PrimerClientCredentials,
): DecodedClientToken {
  if (!credentials) {
    throw new TypeError(
      'Missing required `credentials` in Primer client constructor!',
    );
  }

  if (!credentials.clientToken) {
    throw new TypeError(
      'Missing required `credentials.clientToken` in Primer client constructor!',
    );
  }

  clientTokenHandler.setClientToken(credentials.clientToken);
  const decodedClientToken = clientTokenHandler.getCurrentDecodedClientToken();
  if (!decodedClientToken) {
    throw new TypeError('Cannot parse client token');
  }

  return decodedClientToken;
}

export function getAssetsURL(): string {
  const version = Environment.get('PRIMER_SDK_VERSION');

  return Environment.get(
    'PRIMER_ASSETS_URL',
    Environment.get('PRIMER_BUILD_INTEGRATION_BUILDER', false)
      ? `https://assets.primer.io/primer-sdk-web/ib/${version}`
      : `https://assets.primer.io/primer-sdk-web/${version}`,
  );
}

const getModulesUrl = () => {
  const version = Environment.get('PRIMER_SDK_VERSION', '0.0.0-local');

  return urljoin(
    Environment.get(
      'PRIMER_MODULES_URL',
      'https://assets.primer.io/primer-sdk-web/',
    ),
    version,
  );
};

export const filterPaymentMethodConfigs = (
  paymentMethods: PaymentMethodConfig[],
  renderOptions:
    | CheckoutOptions
    | VaultManagerOptions
    | SinglePaymentMethodCheckoutOptions
    | RenderOptions,
): PaymentMethodConfig[] => {
  if (
    !renderOptions.uxFlow ||
    renderOptions.uxFlow === CheckoutUXFlow.CHECKOUT
  ) {
    paymentMethods = paymentMethods.filter(
      (paymentMethod) =>
        (renderOptions as CheckoutOptions).allowedPaymentMethods?.includes(
          paymentMethod.type,
        ) ?? true,
    );
  } else if (
    renderOptions.uxFlow === CheckoutUXFlow.SINGLE_PAYMENT_METHOD_CHECKOUT
  ) {
    paymentMethods = paymentMethods.filter(
      (paymentMethod) =>
        (renderOptions as SinglePaymentMethodCheckoutOptions).paymentMethod ===
        paymentMethod.type,
    );
  }

  return paymentMethods;
};
